import {Children, forwardRef, Fragment, Ref, ReactElement, ReactNode} from 'react'
import {NonExistentPrimitiveError, usePrimitives} from '@yosmanyga/primitive-ui-spec'
import {UnnormalizedProps as UnnormalizedStyleProps} from '@yosmanyga/core-style'
import {WithTheme} from '@yosmanyga/theme'

type ButtonProps = {
    intent?: string,

    id?: string,
    onClick?: (value: unknown) => void,
    disabled?: boolean,
    progress?: boolean,

    ref?: Ref<unknown>,
    children?: ReactNode,
}

const Button = forwardRef<unknown, ButtonProps & Pick<UnnormalizedStyleProps, 'color' | 'font' | 'pseudo'>>(({
    id,
    disabled, progress,
    color, font, pseudo,
    onClick,
    children,
    ...props
}, ref) => {
    const {Button: BaseButton} = usePrimitives()

    if (!BaseButton) {
        throw new NonExistentPrimitiveError('Button')
    }

    if (progress) {
        disabled = true
    }

    let modifiedChildren = children

    modifiedChildren = fixText(
        modifiedChildren,
    )

    modifiedChildren = separate(modifiedChildren)

    // Needed on Native
    modifiedChildren = passToChildren(modifiedChildren, {
        color: color,
    })

    // Progress is last to avoid "separate" function adding extra margin when there is only an icon
    modifiedChildren = addProgress(modifiedChildren, progress)

    return <BaseButton
        id={id}
        ref={ref}
        color={color}
        font={font}
        pseudo={pseudo}
        flow='row nowrap'
        align={{
            main: 'center',
            cross: 'center',
            self: 'center' // Trick to avoid buttons with full width by default
        }}
        position={progress ? 'relative' : undefined}
        disabled={disabled || progress}
        onClick={(value: unknown) => {
            if (!onClick || progress) {
                return
            }

            onClick(value)
        }}
        {...props}
    >
        {modifiedChildren}
    </BaseButton>
})

const ButtonWithTheme = (props: ButtonProps & UnnormalizedStyleProps) => <WithTheme
    {...props}
    identifier='button'
    Component={Button}
/>

const passToChildren = (children: ReactNode, props: object) => {
  return Children.map(children as ReactElement, (child, i) => {
    return <child.type
      key={i}
      {...props}
      {...child.props}
    />
  })
}

const fixText = (children: ReactNode) => {
    return Children.map(children as ReactElement, (child, i) => {
        if (typeof child.props.children === 'string') {
            // Render it with Fragment (no inner tag) so the hover color will work
            return <child.type
                key={i}
                {...child.props}
                tag={Fragment}
            />
        }

        return <child.type
            key={i}
            {...child.props}
        />
    })
}

const separate = (children: ReactNode) => {
    const count = Children.count(children)

    if (count !== 2) {
        return children
    }

    return Children.map(children as ReactElement, (child: ReactElement, i) => {
        if (typeof child.props.children === 'string') {
            return child
        }

        return <child.type
            key={i}
            margin={{
                left: i === 1 ? 1 / 2 : undefined,
                right: i === 0 ? 1 / 2 : undefined
            }}
            {...child.props}
        />
    })
}

const addProgress = (children: ReactNode, progress?: boolean) => {
    if (!progress) {
        return children
    }

    return [
        Children.map(children, (child) => child),
        <ProgressWithTheme key='progress'/>
    ]
}

const Progress = ({
    ...props
}) => {
    const {Progress: BaseProgress} = usePrimitives()

    if (!BaseProgress) {
        throw new NonExistentPrimitiveError('Progress')
    }

    return <BaseProgress
        position={{
            type: 'absolute',
            // This works for web, but not for mobile.
            // So it's better add this to web theme
            // top: '50%',
            // left: '50%'
        }}
        transform='translate(-50%, -50%)'
        {...props}
    />
}

const ProgressWithTheme = (props: UnnormalizedStyleProps) => <WithTheme
    {...props}
    identifier='button_progress'
    Component={Progress}
/>

export default ButtonWithTheme
